import React, { Component } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Formik, Field, Form } from 'formik';
import ReactLoading from 'react-loading';
import { Redirect } from 'react-router-dom';

import { t } from 'services/translation';

import Api from '../api';
import StarSelector from './StarSelector';
import FeedbackQuestion from './questions';
import {
  Theme,
  Wrapper,
  Label,
  SubLabel,
  Button,
  Section,
  LabelBox,
} from '../styles';

const Loader = <ReactLoading type="spin" height={20} width={20} delay={0} />;

const FormWrapper = styled('div')``;

const Actions = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-top: 1px solid ${Theme.colors.lightLine};

  padding: 50px;
  padding-bottom: 75px;
`;

const Pixel = styled('img')`
  width: 0;
  height: 0;
`;

const textAreaFieldProps = {
  css: css`
    height: 120px;
    width: 100%;
    padding-top: 14px;
    padding-bottom: 14px;
    padding-left: 10px;
    padding-right: 10px;

    box-sizing: border-box;
    border: 1px solid ${Theme.colors.inputs.border};
    border-radius: 0px;
    font-size: 14px;
    background: white;
    resize: none;
  `,
  type: 'textarea',
  component: 'textarea',
};

// Form content
const FeedbackForm = ({
  values,
  isSubmitting,
  brand,
  guestId,
  labelText,
  subLabelText,
  subLabelText2,
  hasResource,
  disableResourceRating,
  resourceRating,
  resourceName,
  resourceTitle,
  experienceQuestions,
  i18n,
  bookingId,
}) => (
  <Form>
    <Wrapper>
      <Section>
        <LabelBox>
          <Label>{labelText}</Label>
          <SubLabel>{subLabelText}</SubLabel>
          <SubLabel>{subLabelText2}</SubLabel>
        </LabelBox>
        <Field name="body" {...textAreaFieldProps} />
      </Section>

      {hasResource && !disableResourceRating ? (
        <Section>
          <LabelBox>
            <Label>
              {`${t('HOW_WOULD_YOU_RATE_YOUR', '', {
                resourceTitle,
                resourceName,
              })}`}
            </Label>
          </LabelBox>
          <Field
            name="rating"
            component={StarSelector}
            rating={resourceRating}
            color={brand.buttonBackgroundColor || Theme.colors.green}
          />
        </Section>
      ) : null}

      {experienceQuestions.map((eq) => (
        <FeedbackQuestion
          key={eq.id}
          question={eq.attributes.question.data}
          experienceQuestionId={eq.attributes.id}
          i18n={i18n}
          color={brand.buttonBackgroundColor}
          value={values[`feedbackQuestion${eq.id}`]}
          bookingId={bookingId}
          guestId={guestId}
        />
      ))}
      <Actions>
        <Button type="submit" backgroundColor={brand.buttonBackgroundColor}>
          {isSubmitting ? Loader : t('SUBMIT')}
        </Button>
      </Actions>
    </Wrapper>
  </Form>
);

// Form logic
class FormContainer extends Component {
  UNSAFE_componentWillMount;

  onSubmitForm = (values, actions) => {
    const { feedback, loadFeedback, history } = this.props;

    Api.updateFeedback(feedback.id, values).then((response) => {
      if (response && response.status === 200) {
        loadFeedback(response.data);
        history.push('/thank_you');
      }

      setTimeout(() => {
        actions.setSubmitting(false);
      }, 3000);
    });
  };

  initialValues = () => {
    const { feedback, experienceQuestions } = this.props;
    const { guestId, score } = feedback.attributes;

    // Default/initial values to populate form (required)
    let initialValues = {
      body: null,
      resourceRating: 0,
      score,
    };

    // Load values off of existing feedback record
    if (feedback) {
      const {
        body,
        resourceRating,
        score: feedbackScore,
      } = feedback.attributes;
      initialValues = {
        body,
        resourceRating,
        score: feedbackScore,
      };
    }
    const experienceQuestionsFields = {};
    if (experienceQuestions) {
      experienceQuestions.forEach((eq) => {
        const { question, questionAnswers } = eq.attributes;
        if (question && question.data.attributes.inputType === 'multi') {
          experienceQuestionsFields[`feedbackQuestion${eq.id}`] =
            questionAnswers.data.map((answer) => ({
              experienceQuestionId: eq.attributes.id,
              questionOptionId: answer.attributes.questionOptionId,
              bookingId: answer.attributes.bookingId,
              id: answer.attributes.id,
              guestId,
            }));
        } else {
          const answer =
            questionAnswers.data && questionAnswers.data[0]
              ? questionAnswers.data[0].attributes
              : {};
          experienceQuestionsFields[`feedbackQuestion${eq.id}`] = {
            experienceQuestionId: eq.attributes.id,
            questionOptionId: answer.questionOptionId,
            textAnswer: answer.textAnswer,
            bookingId: answer.bookingId,
            id: answer.id,
            guestId,
          };
        }
      });
    }
    initialValues = Object.assign(initialValues, experienceQuestionsFields);
    return initialValues;
  };

  render() {
    const { feedback, brand, settings, experienceQuestions } = this.props;
    const { score, booking, resourceRating, guestId } = feedback.attributes;
    const { hasResource, resource } = booking.data.attributes;
    const { disableResourceRating, salesforcePixelSource } = brand;
    const { i18n } = booking.data.attributes;
    const bookingId = booking.data.attributes.id;

    // Admin textFeedbackEnabled settings
    // Skip form if setting is disabled and score is >= 7
    if (!settings.textFeedbackEnabled && score >= 7)
      return <Redirect to="/thank_you" />;

    let labelText = '';
    let subLabelText = '';
    let subLabelText2 = '';

    if (score >= 7) {
      labelText = `${t('WERE_GLAD_YOU_HAD_A_GREAT_TIME')}!`;
      subLabelText = t('PLEASE_TELL_US_WHY_YOU_RATED_US_A', '', { score }); // Fixed 12/9
      subLabelText2 = `${t(
        'DIRECT_FEEDBACK_LIKE_YOURS_IS_INCREDIBLY_IMPORTANT_TO_US'
      )}`;
    } else if (score <= 6) {
      labelText = t('PLEASE_TELL_US_WHY_YOU_RATED_US_A', '', { score }); // Fixed 12/9
    }
    return (
      <FormWrapper>
        <Formik
          render={(formikProps) => (
            <FeedbackForm
              {...formikProps}
              labelText={labelText}
              subLabelText={subLabelText}
              subLabelText2={subLabelText2}
              brand={brand}
              hasResource={hasResource}
              disableResourceRating={disableResourceRating}
              resourceRating={resourceRating}
              resourceName={resource.firstName ? resource.firstName.trim() : ''}
              resourceTitle={resource.singular.toLowerCase().trim()}
              experienceQuestions={experienceQuestions}
              i18n={i18n}
              bookingId={bookingId}
              guestId={guestId}
            />
          )}
          initialValues={this.initialValues()}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values, actions) => this.onSubmitForm(values, actions)}
        />
        <Pixel src={salesforcePixelSource} />
      </FormWrapper>
    );
  }
}

export default FormContainer;
